import {
	ref,
	reactive,
	computed,
	watch,
	onMounted,
	onUnmounted,
	nextTick,
} from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import {
	fetchMercariCategoryList,
	fetchMercariSubCategories,
	type MercariCategoryItem,
} from '@/assets/ts/apiManagement'
import {
	getFilterTrackingValues as getFilterTrackingValuesFromConfig,
	MERCARI_CONSTANTS,
	isValidPrice,
	isValidPriceRange,
	processPriceInput,
} from '@/lib/mercariFilterConfig'
import { getStringWithoutSlash } from '@/lib/utils'

interface MercariApiParams {
	keyword?: string
	page?: number
	limit?: number
	shopId?: string
	sort?: string
	order?: string
	searchLang?: string
	categoryId?: string
	status?: string
	itemConditionId?: string
	priceMin?: string
	priceMax?: string
	marketplace?: string
	[key: string]: any
}

interface FilterState {
	category: string
	itemCondition: string[]
	status: string[]
	price: {
		min: string
		max: string
	}
	marketplace: string[]
	sort: string
}

interface CategoryOption {
	id: string
	label: string
	value: string
}

interface CategoryLevel {
	categoryId: string
	options: CategoryOption[]
	selectedId: string
}

interface MercariFilterOptions {
	productListRef: any
	locationId: string
	filterContent?: any
	keyword?: string
}

function createPriceValidators(
	priceGetter: () => { min: string; max: string },
) {
	const priceMin = computed(() => {
		const price = priceGetter().min
		return price ? parseInt(price, 10) : null
	})

	const priceMax = computed(() => {
		const price = priceGetter().max
		return price ? parseInt(price, 10) : null
	})

	const isPriceInRange = (price: number | null): boolean => {
		if (price === null || price === undefined) return true
		return (
			price >= MERCARI_CONSTANTS.PRICE_LIMITS.MIN &&
			price <= MERCARI_CONSTANTS.PRICE_LIMITS.MAX
		)
	}

	const isMinPriceValid = computed(() => isPriceInRange(priceMin.value))
	const isMaxPriceValid = computed(() => isPriceInRange(priceMax.value))

	const isPriceRangeValid = computed(() => {
		if (!priceMin.value || !priceMax.value) return true
		return priceMin.value <= priceMax.value
	})

	const hasPriceError = computed(() => {
		return (
			!isMinPriceValid.value ||
			!isMaxPriceValid.value ||
			!isPriceRangeValid.value
		)
	})

	return {
		isMinPriceValid,
		isMaxPriceValid,
		isPriceRangeValid,
		hasPriceError,
	}
}

function createCategoryValuesFromLevels(
	categoryLevels: CategoryLevel[],
	maxLevel: number = 4,
) {
	const categoryValues: Record<string, string> = {
		item_category: '',
		item_category2: '',
		item_category3: '',
		item_category4: '',
	}

	for (let i = 0; i < categoryLevels.length && i < maxLevel; i += 1) {
		const level = categoryLevels[i]
		if (level.selectedId !== 'all') {
			const selectedOption = level.options.find(
				opt => opt.value === level.selectedId,
			)
			if (selectedOption) {
				const fieldName = i === 0 ? 'item_category' : `item_category${i + 1}`
				categoryValues[fieldName] = selectedOption.label || ''
			}
		}
	}

	return categoryValues
}

export function useMercariFilter(options: MercariFilterOptions) {
	const keywordRef = ref(options.keyword || '')
	const allCategoryLabel = computed(() => {
		return (
			options?.filterContent?.category?.options?.[0]?.label ||
			MERCARI_CONSTANTS.DEFAULT_VALUES.ALL_CATEGORY_LABEL
		)
	})

	const getCategoryIdFromUrl = (): string | null => {
		const urlParams = new URLSearchParams(window.location.search)
		const categoryId = urlParams.get('category_id')
		return categoryId ? getStringWithoutSlash(categoryId) : null
	}

	const filterState = reactive<FilterState>({
		category: 'all',
		itemCondition: [],
		status: [],
		price: {
			min: '',
			max: '',
		},
		marketplace: [],
		sort: 'recommended',
	})

	const tempFilterState = reactive<FilterState>({
		category: 'all',
		itemCondition: [],
		status: [],
		price: {
			min: '',
			max: '',
		},
		marketplace: [],
		sort: 'recommended',
	})

	const categories = ref<MercariCategoryItem[]>([])
	const isLoadingCategories = ref(false)
	const categoryOptions = ref<CategoryOption[]>([])
	const isLoadingSubCategories = ref(false)
	const lastPriceParams = ref<string>('{}')

	const categoryLevels = ref<CategoryLevel[]>([
		{ categoryId: '', options: [], selectedId: 'all' },
	])

	const effectiveCategoryId = computed(() => {
		for (let i = categoryLevels.value.length - 1; i >= 0; i -= 1) {
			const level = categoryLevels.value[i]
			if (level.selectedId !== 'all') {
				return level.selectedId
			}
		}
		return 'all'
	})

	watch(effectiveCategoryId, newId => {
		filterState.category = newId
	})

	const { isMinPriceValid, isMaxPriceValid, isPriceRangeValid, hasPriceError } =
		createPriceValidators(() => filterState.price)

	const {
		isMinPriceValid: isTempMinPriceValid,
		isMaxPriceValid: isTempMaxPriceValid,
		isPriceRangeValid: isTempPriceRangeValid,
		hasPriceError: hasTempPriceError,
	} = createPriceValidators(() => tempFilterState.price)

	const updateArrayValues = (
		stateArray: string[],
		value: string,
		shouldAdd: boolean,
	) => {
		if (shouldAdd) {
			if (!stateArray.includes(value)) {
				stateArray.push(value)
			}
		} else {
			const index = stateArray.indexOf(value)
			if (index !== -1) {
				stateArray.splice(index, 1)
			}
		}
	}

	const buildApiParams = () => {
		const apiParams: MercariApiParams = {}

		// Category
		if (
			effectiveCategoryId.value &&
			effectiveCategoryId.value !== MERCARI_CONSTANTS.DEFAULT_VALUES.CATEGORY
		) {
			apiParams.categoryId = effectiveCategoryId.value
		}

		// Item Condition
		if (filterState.itemCondition.length > 0) {
			const itemConditionValues = filterState.itemCondition.map(
				id =>
					MERCARI_CONSTANTS.CONDITION_MAP[
						id as keyof typeof MERCARI_CONSTANTS.CONDITION_MAP
					] || id,
			)
			apiParams.itemConditionId = itemConditionValues.join(',')
		}

		// Status (FIXME:BC-9393)
		if (filterState.status.length > 0) {
			const statusValues = [...filterState.status]
			if (
				statusValues.includes('sold_out') &&
				!statusValues.includes('trading')
			) {
				statusValues.push('trading')
			}
			apiParams.status = statusValues.join(',')
		}

		// Price handling: if any non-empty price field is invalid, ignore both prices
		const hasInvalidPrice =
			(filterState.price.min !== '' &&
				filterState.price.min &&
				!isValidPrice(filterState.price.min)) ||
			(filterState.price.max !== '' &&
				filterState.price.max &&
				!isValidPrice(filterState.price.max))

		if (!hasInvalidPrice) {
			if (filterState.price.min && isValidPrice(filterState.price.min)) {
				apiParams.priceMin = filterState.price.min
			}
			if (filterState.price.max && isValidPrice(filterState.price.max)) {
				apiParams.priceMax = filterState.price.max
			}

			const hasBothPrices = apiParams.priceMin && apiParams.priceMax
			if (
				hasBothPrices &&
				!isValidPriceRange(apiParams.priceMin!, apiParams.priceMax!)
			) {
				delete apiParams.priceMin
				delete apiParams.priceMax
			}
		}

		// Marketplace
		if (filterState.marketplace.length > 0) {
			const marketplaceValues = filterState.marketplace.map(
				value =>
					MERCARI_CONSTANTS.MARKETPLACE_MAP[
						value as keyof typeof MERCARI_CONSTANTS.MARKETPLACE_MAP
					] || value,
			)
			apiParams.marketplace = marketplaceValues.join(',')
		}

		if (filterState.sort !== 'recommended') {
			const sortSetting =
				MERCARI_CONSTANTS.SORT_CONFIG[
					filterState.sort as keyof typeof MERCARI_CONSTANTS.SORT_CONFIG
				]
			if (sortSetting) {
				apiParams.sort = sortSetting.sort
				if ('order' in sortSetting) {
					apiParams.order = sortSetting.order
				}
			}
		}

		return apiParams
	}

	watch(
		filterState,
		newState => {
			Object.assign(tempFilterState, JSON.parse(JSON.stringify(newState)))

			// Only update lastPriceParams if no invalid prices and valid range
			const hasInvalidPrice =
				(filterState.price.min !== '' &&
					filterState.price.min &&
					!isValidPrice(filterState.price.min)) ||
				(filterState.price.max !== '' &&
					filterState.price.max &&
					!isValidPrice(filterState.price.max))

			const hasInvalidRange =
				filterState.price.min &&
				filterState.price.max &&
				!isValidPriceRange(filterState.price.min, filterState.price.max)

			if (!hasInvalidPrice && !hasInvalidRange) {
				const currentParams = buildApiParams()
				lastPriceParams.value = JSON.stringify({
					priceMin: currentParams.priceMin,
					priceMax: currentParams.priceMax,
				})
			}
		},
		{ immediate: true },
	)

	const isResetting = ref(false)

	const reloadProductListWithParams = (params: MercariApiParams) => {
		try {
			const productListInstance =
				options.productListRef?.value || options.productListRef

			if (
				productListInstance &&
				typeof productListInstance.fetchProducts === 'function'
			) {
				if (productListInstance.state) {
					productListInstance.state.page = 0
					productListInstance.state.productList = []
				}

				productListInstance.fetchProducts(params)
			}
		} catch (error) {
			console.error(error)
		}
	}

	const reloadProductListImmediately = () => {
		const params = buildApiParams()
		reloadProductListWithParams(params)
	}

	const reloadProductList = useDebounceFn(() => {
		if (isResetting.value) return

		const params = buildApiParams()
		reloadProductListWithParams(params)
	}, MERCARI_CONSTANTS.DEBOUNCE_DELAY)

	const updateCategoryLevels = async (
		levelIndex: number,
		newValue: string,
		isTemp: boolean = false,
	) => {
		if (categoryLevels.value[levelIndex]) {
			categoryLevels.value[levelIndex].selectedId = newValue
		}

		if (newValue !== 'all') {
			categoryLevels.value = categoryLevels.value.slice(0, levelIndex + 1)
			await loadSubCategories(newValue, levelIndex + 1)
		} else {
			categoryLevels.value = categoryLevels.value.slice(0, levelIndex + 1)
		}

		let finalCategoryId = 'all'

		if (isTemp) {
			for (let i = categoryLevels.value.length - 1; i >= 0; i -= 1) {
				const level = categoryLevels.value[i]
				if (level.selectedId !== 'all') {
					tempFilterState.category = level.selectedId
					finalCategoryId = level.selectedId
					break
				}
			}

			if (!tempFilterState.category || tempFilterState.category === '') {
				tempFilterState.category = 'all'
				finalCategoryId = 'all'
			}
		} else {
			if (newValue !== 'all') {
				const hasChildren = await loadSubCategories(newValue, levelIndex + 1)
				if (!hasChildren) {
					filterState.category = newValue
					finalCategoryId = newValue
				} else {
					finalCategoryId = newValue
				}
			} else if (levelIndex === 0) {
				filterState.category = 'all'
				finalCategoryId = 'all'
			} else {
				filterState.category = categoryLevels.value[levelIndex - 1].selectedId
				finalCategoryId = categoryLevels.value[levelIndex - 1].selectedId
			}

			updateCategoryIdInUrl(finalCategoryId)
		}

		const selectedCategories = []
		for (let i = 0; i < categoryLevels.value.length; i += 1) {
			const level = categoryLevels.value[i]
			if (level.selectedId !== 'all') {
				const selectedOption = level.options.find(
					opt => opt.value === level.selectedId,
				)
				selectedCategories.push(selectedOption?.label || '')
			} else if (i <= levelIndex) {
				selectedCategories.push('')
			}
		}

		return selectedCategories
	}

	const handleTempCategoryChange = async (
		event: Event,
		levelIndex: number = 0,
	) => {
		const select = event.target as HTMLSelectElement
		const newValue = select.value

		const selectedCategories = await updateCategoryLevels(
			levelIndex,
			newValue,
			true,
		)

		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: 'Product Listing Page',
			button_name: 'Filtering',
			keyword: keywordRef.value || '',
			item_category: selectedCategories[0] || '',
			item_category2: selectedCategories[1] || '',
			item_category3: selectedCategories[2] || '',
			item_category4: selectedCategories[3] || '',
		})
	}

	const sendFilterTrackingWithCategoryValues = (
		state: FilterState,
		pageName: string = 'Product Listing Page',
	) => {
		const filterValues = getFilterTrackingValuesFromConfig(state)

		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: pageName,
			button_name: 'Filtering',
			keyword: keywordRef.value || '',
			...filterValues,
		})
	}

	const handleTempFilterChange = (
		type: keyof FilterState,
		value: string,
		event: Event,
	) => {
		const { checked } = event.target as HTMLInputElement

		if (type === 'price') return

		if (Array.isArray(tempFilterState[type])) {
			const values = tempFilterState[type] as string[]
			updateArrayValues(values, value, checked)

			if (type !== 'category') {
				sendFilterTrackingWithCategoryValues(tempFilterState)
			}
		}
	}

	const handlePriceUpdate = (
		type: 'min' | 'max',
		event: Event,
		mode: 'desktop' | 'mobile' = 'desktop',
	) => {
		const input = event.target as HTMLInputElement
		const numericValue = processPriceInput(input.value)
		input.value = numericValue

		const targetState = mode === 'desktop' ? filterState : tempFilterState
		targetState.price[type] = numericValue

		if (mode === 'desktop') {
			// Check if any price field is invalid or if price range is invalid
			const hasInvalidPrice =
				(filterState.price.min !== '' &&
					filterState.price.min &&
					!isValidPrice(filterState.price.min)) ||
				(filterState.price.max !== '' &&
					filterState.price.max &&
					!isValidPrice(filterState.price.max))

			const hasInvalidRange =
				filterState.price.min &&
				filterState.price.max &&
				!isValidPriceRange(filterState.price.min, filterState.price.max)

			// Only proceed with API logic if all prices are valid and range is valid
			if (!hasInvalidPrice && !hasInvalidRange) {
				const currentParams = buildApiParams()
				const currentPriceParams = JSON.stringify({
					priceMin: currentParams.priceMin,
					priceMax: currentParams.priceMax,
				})

				if (currentPriceParams !== lastPriceParams.value) {
					lastPriceParams.value = currentPriceParams
					reloadProductList()
					sendFilterTrackingEvent()
				}
			}
			// When there are invalid prices or invalid range, do nothing
		} else {
			sendFilterTrackingWithCategoryValues(tempFilterState)
		}
	}

	const handlePriceChange = (type: 'min' | 'max', event: Event) => {
		handlePriceUpdate(type, event, 'desktop')
	}

	const handleTempPriceChange = (type: 'min' | 'max', event: Event) => {
		handlePriceUpdate(type, event, 'mobile')
	}

	const handleCategoryChange = async (event: Event, levelIndex: number = 0) => {
		const select = event.target as HTMLSelectElement
		const newCategoryId = select.value

		const selectedCategories = await updateCategoryLevels(
			levelIndex,
			newCategoryId,
			false,
		)

		reloadProductList()

		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: 'Product Listing Page',
			button_name: 'Filtering',
			keyword: keywordRef.value || '',
			item_category: selectedCategories[0] || '',
			item_category2: selectedCategories[1] || '',
			item_category3: selectedCategories[2] || '',
			item_category4: selectedCategories[3] || '',
		})
	}

	const handleFilterChange = (
		type: keyof FilterState,
		value: string,
		event: Event,
	) => {
		const { checked } = event.target as HTMLInputElement

		if (type === 'price') return

		if (Array.isArray(filterState[type])) {
			const values = filterState[type] as string[]
			updateArrayValues(values, value, checked)

			reloadProductList()

			if (type !== 'category') {
				sendFilterTrackingEvent()
			}
		}
	}

	const handleSortChange = (event: Event) => {
		const select = event.target as HTMLSelectElement
		filterState.sort = select.value
		reloadProductList()

		let sortingMethodLabel = select.value

		const selectedOption = Array.from(select.options).find(
			option => option.value === select.value,
		)
		if (selectedOption) {
			sortingMethodLabel = selectedOption.textContent || select.value
		}

		const keyword = keywordRef.value

		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: options.locationId,
			button_name: 'Sorting',
			keyword,
			sorting_method: sortingMethodLabel,
		})
	}

	const syncFilterState = (source: FilterState, target: FilterState) => {
		target.category = source.category
		target.itemCondition = [...source.itemCondition]
		target.status = [...source.status]
		target.marketplace = [...source.marketplace]
		target.price.min = source.price.min
		target.price.max = source.price.max
		target.sort = source.sort
	}

	const applyFilters = (newFilterState?: Partial<FilterState>) => {
		try {
			if (newFilterState && Object.keys(newFilterState).length > 0) {
				Object.entries(newFilterState).forEach(([key, value]) => {
					if (!value) return

					const typedKey = key as keyof FilterState
					if (typedKey === 'price' && typeof value === 'object') {
						Object.assign(filterState.price, value)
					} else if (typedKey !== 'price') {
						if (Array.isArray(filterState[typedKey]) && Array.isArray(value)) {
							;(filterState[typedKey] as string[]) = [...value]
						} else if (
							typeof filterState[typedKey] === 'string' &&
							typeof value === 'string'
						) {
							;(filterState[typedKey] as string) = value
						}
					}
				})
			} else {
				for (let i = 0; i < categoryLevels.value.length; i += 1) {
					if (i > 0 && tempFilterState.category === 'all') {
						categoryLevels.value = categoryLevels.value.slice(0, 1)
						break
					}
				}

				syncFilterState(tempFilterState, filterState)
			}

			if (hasPriceError.value) {
				return
			}

			updateCategoryIdInUrl(filterState.category)

			reloadProductList()

			sendFilterTrackingEvent()
		} catch (error) {
			console.error(error)
		}
	}

	const clearAllFilters = (
		skipReload = false,
		includingSort = true,
		skipGA = false,
	) => {
		isResetting.value = true

		const currentSort = filterState.sort

		const defaultState = {
			category: MERCARI_CONSTANTS.DEFAULT_VALUES.CATEGORY,
			itemCondition: [],
			status: [],
			price: { min: '', max: '' },
			marketplace: [],
			sort: includingSort ? MERCARI_CONSTANTS.DEFAULT_VALUES.SORT : currentSort,
		}

		Object.assign(tempFilterState, defaultState)
		Object.assign(filterState, defaultState)

		const firstLevelOptions = categoryLevels.value[0]?.options || []
		categoryLevels.value = [
			{
				categoryId: '',
				options: firstLevelOptions,
				selectedId: MERCARI_CONSTANTS.DEFAULT_VALUES.CATEGORY,
			},
		]

		filterState.category = MERCARI_CONSTANTS.DEFAULT_VALUES.CATEGORY
		tempFilterState.category = MERCARI_CONSTANTS.DEFAULT_VALUES.CATEGORY

		updateCategoryIdInUrl(MERCARI_CONSTANTS.DEFAULT_VALUES.CATEGORY)
		isResetting.value = false

		if (!skipReload) {
			const productListInstance =
				options.productListRef?.value || options.productListRef

			if (productListInstance?.fetchProducts) {
				if (productListInstance.state) {
					productListInstance.state.page = 0
					productListInstance.state.productList = []
				}

				nextTick(() => {
					const apiParams = buildApiParams()

					if (productListInstance.keyword) {
						apiParams.keyword = productListInstance.keyword
					}
					if (productListInstance.shopId) {
						apiParams.shopId = productListInstance.shopId
					}

					productListInstance.fetchProducts(apiParams)
				})
			}
		}

		if (!skipGA && !skipReload) {
			createGAEvent(GaGeneralEvent.ButtonClick, {
				page_name: options.locationId,
				button_name: 'Clear Filters',
				feature: 'filter',
			})
		}
	}

	const clearFiltersOnly = (skipReload = false, skipGA = false) => {
		clearAllFilters(skipReload, false, skipGA)
	}

	const checkTempPriceError = () => hasTempPriceError.value

	const createCategoryOptions = (
		categoryList: MercariCategoryItem[],
	): CategoryOption[] => [
		{ id: 'all', label: allCategoryLabel.value, value: 'all' },
		...categoryList.map(cat => ({
			id: cat.id,
			label: cat.name,
			value: cat.id,
		})),
	]

	const fetchCategories = async () => {
		try {
			isLoadingCategories.value = true
			const categoryData = await fetchMercariCategoryList()
			categories.value = categoryData.category || []

			if (categories.value.length === 0) return

			const optionsList = createCategoryOptions(categories.value)

			categoryLevels.value[0].options = optionsList
			categoryOptions.value = optionsList

			if (options?.filterContent?.category) {
				options.filterContent.category.options = optionsList
			}
		} catch (error) {
			console.error('Failed to fetch Mercari categories:', error)
		} finally {
			isLoadingCategories.value = false
		}
	}

	const setCategoryLevel = (
		levelIndex: number,
		categoryId: string,
		levelOptions: CategoryOption[],
		selectedId: string = 'all',
	) => {
		const levelData = { categoryId, options: levelOptions, selectedId }

		if (levelIndex >= categoryLevels.value.length) {
			categoryLevels.value.push(levelData)
		} else {
			categoryLevels.value[levelIndex] = levelData
		}
	}

	const fetchCategoryInfo = async (
		categoryId: string,
	): Promise<{ category: MercariCategoryItem[]; parent?: any } | null> => {
		try {
			const cleanCategoryId = getStringWithoutSlash(categoryId)
			const result = await fetchMercariSubCategories(cleanCategoryId)
			return result || null
		} catch (error) {
			console.error('Failed to fetch category info:', error)
			return null
		}
	}

	const buildCategoryPathFromParent = (parentStructure: any): string[] => {
		const path: string[] = []
		let current = parentStructure

		while (current) {
			path.unshift(current.id)
			current = current.parent
		}

		return path
	}

	const buildCategoryPath = async (
		targetCategoryId: string,
	): Promise<string[]> => {
		try {
			const categoryInfo = await fetchCategoryInfo(targetCategoryId)

			if (!categoryInfo || !categoryInfo.parent) {
				return [targetCategoryId]
			}

			const parentPath = buildCategoryPathFromParent(categoryInfo.parent)
			const fullPath = [...parentPath, targetCategoryId]
			return fullPath
		} catch (error) {
			console.error('Failed to build category path:', error)
			return [targetCategoryId]
		}
	}

	const initializeCategoryLevels = async (categoryPath: string[]) => {
		try {
			isLoadingCategories.value = true

			await fetchCategories()

			// Process categories sequentially using reduce to avoid await-in-loop
			await categoryPath.reduce(async (previousPromise, categoryId, i) => {
				await previousPromise

				const parentCategoryId = i === 0 ? null : categoryPath[i - 1]

				if (i === 0) {
					if (categoryLevels.value[0]) {
						categoryLevels.value[0].selectedId = categoryId
					}
				} else {
					const hasSubCategories = await loadSubCategories(parentCategoryId!, i)
					if (hasSubCategories && categoryLevels.value[i]) {
						categoryLevels.value[i].selectedId = categoryId
					}
				}
			}, Promise.resolve())

			const lastCategoryId = categoryPath[categoryPath.length - 1]
			await loadSubCategories(lastCategoryId, categoryPath.length)

			filterState.category = categoryPath[categoryPath.length - 1]
		} catch (error) {
			console.error('Failed to initialize category levels:', error)
		} finally {
			isLoadingCategories.value = false
		}
	}

	const loadSubCategories = async (
		categoryId: string,
		levelIndex: number,
	): Promise<boolean> => {
		const cleanCategoryId = getStringWithoutSlash(categoryId)
		if (!cleanCategoryId || cleanCategoryId === 'all') return false

		try {
			isLoadingSubCategories.value = true
			const result = await fetchMercariSubCategories(cleanCategoryId)

			const subCategories = result.category || []

			if (subCategories.length === 0) return false

			const categoryOptionsList = createCategoryOptions(subCategories)
			setCategoryLevel(levelIndex, cleanCategoryId, categoryOptionsList)

			return true
		} catch (error) {
			console.error('Failed to fetch Mercari sub-categories:', error)
			return false
		} finally {
			isLoadingSubCategories.value = false
		}
	}

	const initialize = async () => {
		if (categoryLevels.value[0].options.length > 0) return

		categoryLevels.value[0].options = [
			{ id: 'all', label: allCategoryLabel.value, value: 'all' },
		]

		const urlCategoryId = getCategoryIdFromUrl()

		if (urlCategoryId && urlCategoryId !== 'all') {
			try {
				const categoryPath = await buildCategoryPath(urlCategoryId)
				await initializeCategoryLevels(categoryPath)
			} catch (error) {
				console.error('Failed to initialize category from URL:', error)
				await fetchCategories()
			}
		} else {
			await fetchCategories()
		}
	}

	const updateKeyword = (newKeyword: string) => {
		keywordRef.value = newKeyword
	}

	const getFilterTrackingValues = (state: FilterState = filterState) => {
		return getFilterTrackingValuesFromConfig(state)
	}

	const sendFilterTrackingEvent = () => {
		const filterValues = getFilterTrackingValues(filterState)

		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: 'Product Listing Page',
			button_name: 'Filtering',
			keyword: keywordRef.value || '',
			...filterValues,
		})
	}

	const handlePopState = async () => {
		const urlCategoryId = getCategoryIdFromUrl()

		if (urlCategoryId && urlCategoryId !== 'all') {
			try {
				const categoryPath = await buildCategoryPath(urlCategoryId)
				await initializeCategoryLevels(categoryPath)
			} catch (error) {
				console.error('Failed to handle popstate category change:', error)
			}
		} else {
			clearAllFilters(true)
		}
	}

	onMounted(() => {
		window.addEventListener('popstate', handlePopState)
	})

	onUnmounted(() => {
		window.removeEventListener('popstate', handlePopState)
	})

	const updateCategoryIdInUrl = (categoryId: string) => {
		const url = new URL(window.location.href)

		if (categoryId && categoryId !== 'all') {
			url.searchParams.set('category_id', categoryId)
		} else {
			url.searchParams.delete('category_id')
		}

		window.history.pushState({ categoryId }, '', url.toString())
	}

	return {
		filterState,
		tempFilterState,
		isMinPriceValid,
		isMaxPriceValid,
		isPriceRangeValid,
		hasPriceError,
		isTempMinPriceValid,
		isTempMaxPriceValid,
		isTempPriceRangeValid,
		hasTempPriceError,

		categories,
		isLoadingCategories,
		categoryOptions,
		isLoadingSubCategories,
		categoryLevels,
		effectiveCategoryId,
		fetchCategories,
		loadSubCategories,
		initialize,

		// desktop
		handleCategoryChange,
		handleFilterChange,
		handlePriceChange,
		handleSortChange,

		// tablet/mobile
		handleTempCategoryChange,
		handleTempFilterChange,
		handleTempPriceChange,

		checkTempPriceError,

		clearAllFilters,
		clearFiltersOnly,
		applyFilters,
		reloadProductList,
		reloadProductListImmediately,
		buildApiParams,

		updateKeyword,

		sendFilterTrackingEvent,
		getFilterTrackingValues,
		createCategoryValuesFromLevels,

		useFilterPanelLogic: () => {
			const handleCheckboxChange = (data: {
				type: keyof FilterState
				id: string
				checked: boolean
			}) => {
				const { type, id, checked } = data
				const event = { target: { checked } } as unknown as Event
				handleTempFilterChange(type, id, event)
			}

			const handleCategorySelect = (data: { e: Event; levelIndex: number }) => {
				const { e, levelIndex } = data
				handleTempCategoryChange(e, levelIndex)
			}

			const handleClear = (emit?: (event: string, value: boolean) => void) => {
				clearFiltersOnly(false, true)
				if (emit) {
					emit('update:isOpen', false)
				}
			}

			const handleApply = (emit: (event: string, value: boolean) => void) => {
				let hasError = false

				if (hasTempPriceError.value) {
					hasError = true
				}

				if (hasError) {
					return false
				}

				applyFilters()
				emit('update:isOpen', false)
				return true
			}

			const handleOpenChange = async (
				value: boolean,
				emit: (event: string, value: boolean) => void,
				immediateClose = false,
			) => {
				if (value && categoryLevels.value[0].options.length <= 1) {
					await initialize()
				}

				if (immediateClose && !value) {
					emit('update:isOpen', false)
					return
				}

				emit('update:isOpen', value)
			}

			return {
				handleCheckboxChange,
				handleCategorySelect,
				handleClear,
				handleApply,
				handleOpenChange,
			}
		},
	}
}
